// SDK-based API implementation
import {
  auth,
  user,
  data,
  ai,
  admin,
  dataSources,
  setConfig,
  salesforce,
} from "@startupp-cloud/startupp-ai-sdk";

// ✅ Production-ready: No console logging - defined once at module level
const isDevelopment = process.env.NODE_ENV === "development";

// SDK is already configured in src/index.js
// Configure SDK with API base URLs
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.startupp.ai";
const AUTH_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.startupp.ai";

// Note: setConfig is already called in src/index.js

// ✅ Production-ready event emitter for auth state changes (maintaining compatibility)
export const authEvents = {
  listeners: {},
  subscribe(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
    return () => {
      const index = this.listeners[event].indexOf(callback);
      if (index > -1) {
        this.listeners[event].splice(index, 1);
      }
    };
  },
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          // Send to error reporting service in production
          if (!isDevelopment && window.errorReporting) {
            window.errorReporting.captureException(error);
          }
        }
      });
    }
  },
};

// ✅ Production-ready: Set tokens using SDK methods
export const setTokens = ({ accessToken, refreshToken }) => {
  try {
    auth.setTokens({ accessToken, refreshToken });
    authEvents.emit("tokensChanged", { accessToken, refreshToken });
  } catch (error) {
    authEvents.emit("authError", { method: "setTokens", error });
    throw error;
  }
};

// ✅ Production-ready: Get tokens from SDK
export const getTokens = () => {
  try {
    return auth.getTokens();
  } catch (error) {
    authEvents.emit("authError", { method: "getTokens", error });
    throw error;
  }
};

// ✅ Production-ready: Auth Methods using SDK's smart authentication
export const loginSmart = async () => {
  try {
    const result = await auth.loginSmart();
    if (result.accessToken) {
      setTokens({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      });
    }
    authEvents.emit("tokensChanged", result);
    return result;
  } catch (error) {
    authEvents.emit("authError", { method: "loginSmart", error });
    throw error;
  }
};

export const login = async () => {
  try {
    authEvents.emit("loginRequested");
    const result = await auth.loginSmart();
    if (result.accessToken) {
      setTokens({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      });
    }
    authEvents.emit("tokensChanged", result);
    return result;
  } catch (error) {
    authEvents.emit("authError", { method: "login", error });
    throw error;
  }
};

export const logout = async () => {
  try {
    await auth.advancedLogout();
    authEvents.emit("logout");
  } catch (error) {
    authEvents.emit("authError", { method: "logout", error });
    throw error;
  }
};

export const getUserInfo = async () => {
  try {
    // Check if we have valid tokens first
    const tokens = getTokens();
    if (!tokens.accessToken) {
      return null;
    }

    const userInfo = await user.getUserInfo();

    if (userInfo) {
      // Store user info for compatibility
      const userId =
        userInfo.userId ||
        userInfo.email ||
        userInfo.id ||
        userInfo.sub ||
        userInfo._id ||
        "";
      localStorage.setItem("userId", userId);
      localStorage.setItem("userInfo", JSON.stringify(userInfo));
      return userInfo;
    } else {
      return null;
    }
  } catch (error) {
    // Check if it's an authentication error
    if (error.response?.status === 401) {
      // Use SDK's advancedLogout for complete cleanup
      await auth.advancedLogout();
      authEvents.emit("unauthorized", { error });
    }

    // Emit error event instead of console logging
    authEvents.emit("authError", { method: "getUserInfo", error });
    return null;
  }
};

// ✅ Production-ready: AI Operations
export const askAI = async (prompt, contextId, contextPlatform) => {
  try {
    return await ai.askAI(prompt, contextId, contextPlatform);
  } catch (error) {
    if (!isDevelopment && window.errorReporting) {
      window.errorReporting.captureException(error);
    }
    throw error;
  }
};

export const trainAI = async (posts) => {
  try {
    return await ai.trainAI(posts);
  } catch (error) {
    if (!isDevelopment && window.errorReporting) {
      window.errorReporting.captureException(error);
    }
    throw error;
  }
};

const getContext = (context = {}) => ({
  organizationId: process.env.REACT_APP_DEFAULT_ORGANIZATION_ID,
  apiKey: process.env.REACT_APP_APIKEY,
});

// ✅ Production-ready: Salesforce Operations
export const sfQuery = async (obj, fields, filter, context = {}) => {
  try {
    return (await salesforce.sfQuery(obj, fields, filter, getContext(context)))
      .records;
  } catch (error) {
    if (!isDevelopment && window.errorReporting) {
      window.errorReporting.captureException(error);
    }
    throw error;
  }
};

export const sfRest = async (name, svcName, action, requestKeys, context) => {
  try {
    return (
      await salesforce.sfRest(
        name,
        svcName,
        action,
        requestKeys,
        getContext(context)
      )
    ).data;
  } catch (error) {
    if (!isDevelopment && window.errorReporting) {
      window.errorReporting.captureException(error);
    }
    throw error;
  }
};

export const query = async (collections, context) => {
  try {
    return await data.queryOrganizationData(collections);
  } catch (error) {
    if (!isDevelopment && window.errorReporting) {
      window.errorReporting.captureException(error);
    }
    throw error;
  }
};

export const sfGlobalSearch = async (search, context) => {
  try {
    const organizationId =
      context?.organizationId || process.env.REACT_APP_DEFAULT_ORGANIZATION_ID;
    return await dataSources.salesforce.search(organizationId, search);
  } catch (error) {
    if (!isDevelopment && window.errorReporting) {
      window.errorReporting.captureException(error);
    }
    throw error;
  }
};

// ✅ Production-ready: Auth capability detection using SDK
export const getAuthCapabilities = () => {
  try {
    return auth.getAuthCapabilities();
  } catch (error) {
    return {
      canUsePopup: true,
      canUseRedirect: true,
      isInIframe: window !== window.top,
      isMobile:
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        ),
      supportsPopups: true,
      recommendedMethod: window !== window.top ? "popup" : "redirect",
    };
  }
};

// Export SDK modules for direct use
export { auth, user, data, ai, admin, dataSources };

// Export everything the current implementation exports
export { API_BASE_URL, AUTH_BASE_URL };
